import React, { useState, useEffect } from 'react';
import { getRevenueReport, getCollectionsReport, getAgingReport, getDocumentExpiryReport, getVatReport, getServiceOrdersReport } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  FileText,
  Calendar,
  PieChart,
  Download,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '@/lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell
} from 'recharts';

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState('revenue');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 3)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  // Report data states
  const [revenueData, setRevenueData] = useState(null);
  const [collectionsData, setCollectionsData] = useState(null);
  const [agingData, setAgingData] = useState(null);
  const [expiryData, setExpiryData] = useState(null);
  const [vatData, setVatData] = useState(null);
  const [ordersData, setOrdersData] = useState(null);

  const loadReport = async (reportType) => {
    setLoading(true);
    try {
      switch (reportType) {
        case 'revenue':
          const rev = await getRevenueReport({ start_date: dateRange.start, end_date: dateRange.end });
          setRevenueData(rev.data);
          break;
        case 'collections':
          const col = await getCollectionsReport({ start_date: dateRange.start, end_date: dateRange.end });
          setCollectionsData(col.data);
          break;
        case 'aging':
          const age = await getAgingReport();
          setAgingData(age.data);
          break;
        case 'expiry':
          const exp = await getDocumentExpiryReport({ days_ahead: 90 });
          setExpiryData(exp.data);
          break;
        case 'vat':
          const vat = await getVatReport({ start_date: dateRange.start, end_date: dateRange.end });
          setVatData(vat.data);
          break;
        case 'orders':
          const ord = await getServiceOrdersReport({ start_date: dateRange.start, end_date: dateRange.end });
          setOrdersData(ord.data);
          break;
      }
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport(activeTab);
  }, [activeTab]);

  const handleRefresh = () => {
    loadReport(activeTab);
  };

  const exportToCsv = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => JSON.stringify(row[h] ?? '')).join(','))
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-muted-foreground">Business analytics and insights</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-sm">From:</Label>
            <Input
              type="date"
              value={dateRange.start}
              onChange={(e) => setDateRange(p => ({ ...p, start: e.target.value }))}
              className="w-36"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To:</Label>
            <Input
              type="date"
              value={dateRange.end}
              onChange={(e) => setDateRange(p => ({ ...p, end: e.target.value }))}
              className="w-36"
            />
          </div>
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="revenue" className="flex items-center gap-1">
            <TrendingUp className="w-4 h-4" /> Revenue
          </TabsTrigger>
          <TabsTrigger value="collections" className="flex items-center gap-1">
            <DollarSign className="w-4 h-4" /> Collections
          </TabsTrigger>
          <TabsTrigger value="aging" className="flex items-center gap-1">
            <FileText className="w-4 h-4" /> Aging
          </TabsTrigger>
          <TabsTrigger value="expiry" className="flex items-center gap-1">
            <Calendar className="w-4 h-4" /> Expiry
          </TabsTrigger>
          <TabsTrigger value="vat" className="flex items-center gap-1">
            <PieChart className="w-4 h-4" /> VAT
          </TabsTrigger>
          <TabsTrigger value="orders" className="flex items-center gap-1">
            <BarChart3 className="w-4 h-4" /> Orders
          </TabsTrigger>
        </TabsList>

        {/* Revenue Report */}
        <TabsContent value="revenue" className="mt-6">
          {revenueData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Invoiced</p>
                    <p className="text-3xl font-semibold font-mono text-blue-500">
                      {formatCurrency(revenueData.summary.total_invoiced)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Collected</p>
                    <p className="text-3xl font-semibold font-mono text-green-500">
                      {formatCurrency(revenueData.summary.total_collected)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Collection Rate</p>
                    <p className="text-3xl font-semibold">
                      {revenueData.summary.collection_rate.toFixed(1)}%
                    </p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Revenue Trend</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => exportToCsv(revenueData.data, 'revenue_report')}>
                    <Download className="w-4 h-4 mr-1" /> Export
                  </Button>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueData.data}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Bar dataKey="invoiced" fill="#3b82f6" name="Invoiced" />
                      <Bar dataKey="collected" fill="#22c55e" name="Collected" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Collections Report */}
        <TabsContent value="collections" className="mt-6">
          {collectionsData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Collected</p>
                    <p className="text-3xl font-semibold font-mono text-green-500">
                      {formatCurrency(collectionsData.total_collected)}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      {collectionsData.payment_count} payments
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">By Payment Method</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={200}>
                      <RechartsPie>
                        <Pie
                          data={collectionsData.by_method}
                          dataKey="amount"
                          nameKey="method"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ method, percent }) => `${method} ${(percent * 100).toFixed(0)}%`}
                        >
                          {collectionsData.by_method.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Payment Methods Breakdown</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => exportToCsv(collectionsData.by_method, 'collections_report')}>
                    <Download className="w-4 h-4 mr-1" /> Export
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Method</TableHead>
                        <TableHead className="text-right">Count</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {collectionsData.by_method.map((m) => (
                        <TableRow key={m.method || m._id}>
                          <TableCell className="font-medium">{m.method}</TableCell>
                          <TableCell className="text-right">{m.count}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(m.amount)}</TableCell>
                          <TableCell className="text-right">
                            {((m.amount / collectionsData.total_collected) * 100).toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Aging Report */}
        <TabsContent value="aging" className="mt-6">
          {agingData && (
            <div className="space-y-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <p className="text-sm text-muted-foreground">Total Outstanding</p>
                      <p className="text-3xl font-semibold font-mono text-red-500">
                        {formatCurrency(agingData.total_outstanding)}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => {
                      const allInvoices = agingData.buckets.flatMap(b => b.invoices);
                      exportToCsv(allInvoices, 'aging_report');
                    }}>
                      <Download className="w-4 h-4 mr-1" /> Export
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-4">
                    {agingData.buckets.map((bucket) => (
                      <div key={bucket.label} className="p-4 rounded-lg bg-secondary/50">
                        <p className="text-sm text-muted-foreground">{bucket.label}</p>
                        <p className="text-xl font-semibold font-mono">{formatCurrency(bucket.amount)}</p>
                        <p className="text-xs text-muted-foreground">{bucket.count} invoices</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {agingData.buckets.map((bucket) => (
                bucket.invoices.length > 0 && (
                  <Card key={bucket.label}>
                    <CardHeader>
                      <CardTitle className="text-base">{bucket.label}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Invoice</TableHead>
                            <TableHead>Client</TableHead>
                            <TableHead className="text-right">Balance</TableHead>
                            <TableHead className="text-right">Days Overdue</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {bucket.invoices.slice(0, 10).map((inv) => (
                            <TableRow key={inv.invoice_number || inv.id}>
                              <TableCell className="font-mono">{inv.invoice_number}</TableCell>
                              <TableCell>{inv.client_name}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(inv.balance_due)}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant={inv.days_overdue > 60 ? 'destructive' : 'secondary'}>
                                  {inv.days_overdue}d
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {bucket.invoices.length > 10 && (
                        <p className="text-sm text-muted-foreground mt-2">
                          +{bucket.invoices.length - 10} more invoices
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )
              ))}
            </div>
          )}
        </TabsContent>

        {/* Document Expiry Report */}
        <TabsContent value="expiry" className="mt-6">
          {expiryData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="border-red-500/50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Expired</p>
                    <p className="text-3xl font-semibold text-red-500">{expiryData.summary.expired}</p>
                  </CardContent>
                </Card>
                <Card className="border-orange-500/50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Expiring in 7 Days</p>
                    <p className="text-3xl font-semibold text-orange-500">{expiryData.summary.expiring_7_days}</p>
                  </CardContent>
                </Card>
                <Card className="border-yellow-500/50">
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Expiring in 30 Days</p>
                    <p className="text-3xl font-semibold text-yellow-500">{expiryData.summary.expiring_30_days}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Expiring in 90 Days</p>
                    <p className="text-3xl font-semibold">{expiryData.summary.expiring_90_days}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Document Expiry List</CardTitle>
                  <Button variant="outline" size="sm" onClick={() => exportToCsv(expiryData.documents, 'expiry_report')}>
                    <Download className="w-4 h-4 mr-1" /> Export
                  </Button>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Document</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Owner</TableHead>
                        <TableHead>Expiry Date</TableHead>
                        <TableHead className="text-right">Days</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expiryData.documents.slice(0, 50).map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell className="font-mono">{doc.document_number}</TableCell>
                          <TableCell>{doc.doc_type_name}</TableCell>
                          <TableCell className="font-medium">{doc.owner_name}</TableCell>
                          <TableCell>{formatDate(doc.expiry_date)}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={
                              doc.days_until_expiry < 0 ? 'destructive' :
                              doc.days_until_expiry <= 7 ? 'warning' :
                              doc.days_until_expiry <= 30 ? 'default' : 'secondary'
                            }>
                              {doc.days_until_expiry}d
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={doc.status === 'Expired' ? 'destructive' : 'secondary'}>
                              {doc.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* VAT Report */}
        <TabsContent value="vat" className="mt-6">
          {vatData && (
            <div className="space-y-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle>VAT Summary</CardTitle>
                    <CardDescription>
                      Period: {vatData.period.start} to {vatData.period.end}
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => exportToCsv(vatData.by_month, 'vat_report')}>
                    <Download className="w-4 h-4 mr-1" /> Export
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground">Taxable Amount</p>
                      <p className="text-2xl font-semibold font-mono">
                        {formatCurrency(vatData.summary.total_taxable_amount)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground">VAT Collected</p>
                      <p className="text-2xl font-semibold font-mono text-primary">
                        {formatCurrency(vatData.summary.total_vat_collected)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground">Gross Amount</p>
                      <p className="text-2xl font-semibold font-mono">
                        {formatCurrency(vatData.summary.total_gross_amount)}
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-secondary/50">
                      <p className="text-sm text-muted-foreground">Invoice Count</p>
                      <p className="text-2xl font-semibold">{vatData.summary.invoice_count}</p>
                    </div>
                  </div>

                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Taxable</TableHead>
                        <TableHead className="text-right">VAT (5%)</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vatData.by_month.map((m, idx) => (
                        <TableRow key={m.method || m._id}>
                          <TableCell className="font-medium">{m.month}</TableCell>
                          <TableCell className="text-right font-mono">{formatCurrency(m.taxable)}</TableCell>
                          <TableCell className="text-right font-mono text-primary">{formatCurrency(m.vat)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Service Orders Report */}
        <TabsContent value="orders" className="mt-6">
          {ordersData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Orders</p>
                    <p className="text-3xl font-semibold">{ordersData.summary.total_orders}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Total Value</p>
                    <p className="text-3xl font-semibold font-mono">
                      {formatCurrency(ordersData.summary.total_value)}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Avg Order Value</p>
                    <p className="text-3xl font-semibold font-mono">
                      {formatCurrency(ordersData.summary.avg_order_value)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">By Status</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <RechartsPie>
                        <Pie
                          data={ordersData.by_status}
                          dataKey="count"
                          nameKey="status"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ status, count }) => `${status}: ${count}`}
                        >
                          {ordersData.by_status.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPie>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Top Services</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Service</TableHead>
                          <TableHead className="text-right">Orders</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ordersData.top_services.slice(0, 5).map((s, idx) => (
                          <TableRow key={m.method || m._id}>
                            <TableCell className="font-medium">{s.service}</TableCell>
                            <TableCell className="text-right">{s.count}</TableCell>
                            <TableCell className="text-right font-mono">{formatCurrency(s.value)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
